import React, { cloneElement, useEffect, useMemo, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { FolderClosedIcon, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '../common/text-button';
import { IconButton } from '../common/icon-button';
import { LoadingOverlay } from '../common/loading-overlay';
import { KnowledgeFolderData } from '@/services/knowledge-list-folders';
import { Image } from '../common/image';
import knowledgeBg from '@/assets/assets/knowledge-bg.webp';
import { CheckboxCheckedDisabledIcon, CheckboxCheckedIcon, CheckboxUncheckedIcon } from '../assets-knowledge-item/assets';
import { TruncatedText } from '../common/truncated-text';

interface KnowledgeChooseModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  knowledgeFolders?: KnowledgeFolderData[];
  title?: string;
  loading?: boolean;
  selectedIds?: number[];
  disabledIds?: number[];
  confirmText?: string;
  onConfirm?: (folderIds: number[]) => Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

/** 文件夹移动/移除弹窗 */
export const KnowledgeChooseModal: React.FC<KnowledgeChooseModalProps> = ({
  trigger,
  open: externalOpen,
  title,
  knowledgeFolders,
  loading,
  selectedIds: externalSelectedIds,
  disabledIds,
  confirmText,
  onConfirm,
  onOpenChange,
}) => {
  const { t } = useTranslation();

  const [internalOpen, setInternalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [selectedIds, setSelectedIds] = useState(externalSelectedIds || []);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  useEffect(() => {
    setSelectedIds(externalSelectedIds || []);
  }, [externalSelectedIds])

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    if (!open) {
      setSelectedIds([]);
      setActiveTab(1);
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 左侧导航
  const tabs = useMemo(() => [
    {
      id: 1,
      label: t('assets.knowledge_item.move_modal.all'),
      value: knowledgeFolders?.length || 0,
    },
    {
      id: 2,
      label: t('assets.knowledge_item.move_modal.selected'),
      value: selectedIds.length,
    },
  ], [selectedIds, knowledgeFolders])

  // 选择标签
  const handleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  // 复制
  const handleConfirm = async () => {
    if (selectedIds.length === 0) return;
    await onConfirm?.(selectedIds);
    handleClose();
  }

  // 选中的文件夹
  const selectedFolders = useMemo(() => {
    if (activeTab === 1) return knowledgeFolders || [];
    return selectedIds.map(id => ({
      id,
      folderName: knowledgeFolders?.find(folder => folder.id === id)?.folderName || '',
    }));
  }, [activeTab, selectedIds, knowledgeFolders])

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay
        className={cn("fixed inset-0 bg-black/50 z-50")}
      />
      <Dialog.Content
        modal-tag-move
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[720px] max-h-[544px] min-h-[544px] flex-shrink-0",
          "z-[50] overflow-hidden"
        )}
      >
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4 border-b border-[rgba(234,236,240,1)]">
          <Dialog.Title
            className='focus:outline-none text-[rgba(16,24,40,1)] text-base font-semibold flex items-center'
          >
            <span>{title || t('agent_creation.subscription_settings.add')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <IconButton
              icon={<X className="size-6 text-[rgba(71,84,103,1)]" />}
              onClick={handleClose}
            />
          </Dialog.Close>
        </div>
        <div className='flex-1 flex select-none min-h-0'>
          {/* 左侧导航 */}
          <div className='relative w-full flex flex-1'>
            <div className='flex flex-col gap-1 max-w-[190px] min-w-[190px] p-1.5 border-r border-[rgba(234,236,240,1)]'>
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  className={cn(
                    'w-full flex items-center justify-between rounded-[6px] text-sm font-medium px-3 py-2  cursor-pointer transition-all',
                    activeTab === tab.id ? 'bg-[rgba(16,24,40,0.05)]' : 'hover:bg-[rgba(16,24,40,0.02)]'
                  )}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className='text-[rgba(16,24,40,1)]'>{tab.label}</span>
                  <span className='text-[rgba(102,112,133,1)]'>{tab.value}</span>
                </div>
              ))}
            </div>
          </div>
          {/* 右侧内容区域 */}
          <div className='w-full flex flex-col'>
            <div className='flex flex-1 flex-col gap-1 overflow-y-auto p-3 '>
              {/* 知识库文件夹列表 */}
              {(activeTab === 1 ? knowledgeFolders : selectedFolders)?.map(folder => (
                <div
                  className={cn(
                    'w-full h-[55px] rounded-[8px] p-3 flex items-center gap-3 transition-all hover:bg-[rgba(16,24,40,0.05)] cursor-pointer',
                    disabledIds?.includes(folder.id) && 'cursor-not-allowed opacity-75',
                    selectedIds.includes(folder.id) && 'bg-[rgba(16,24,40,0.05)]'
                  )}

                  onClick={() => {
                    if (disabledIds?.includes(folder.id)) return;
                    handleSelect(folder.id)
                  }}
                >
                  {disabledIds?.includes(folder.id) ?
                    <CheckboxCheckedDisabledIcon className="size-5 flex-shrink-0" />
                    : selectedIds.includes(folder.id) ? (
                      <CheckboxCheckedIcon className="size-5 flex-shrink-0" />
                    ) : (
                      <CheckboxUncheckedIcon className="size-5 flex-shrink-0" />
                    )}
                  <Image src={knowledgeBg} className='w-[30px] h-[30px] flex-shrink-0' />
                  <TruncatedText text={folder.folderName} className='w-[380px] text-[rgba(16,24,40,1)] text-sm' />
                </div>
              ))}
              {/* 空状态处理：当选择页签没有数据时 */}
              {activeTab === 2 && selectedIds.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm py-10">
                  <FolderClosedIcon className="size-10 mb-2 opacity-20" />
                  <p>{t('agent_creation.subscription_settings.add_remove_modal.knowledge_no_selected')}</p>
                </div>
              )}
            </div>
            <div className='w-full flex justify-end items-center space-x-3 border-t border-[rgba(234,236,240,1)]  px-[11px]  py-3'>
              <TextButton
                label={t("agent_creation.subscription_settings.add_remove_modal.cancel")}
                className='h-9 w-[120px] text-sm  bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-medium !text-[rgba(65,70,81,1)] !px-10'
                onClick={handleClose}
              />
              <TextButton
                label={(confirmText || t("agent_creation.subscription_settings.add_remove_modal.confirm")) + ` (${selectedIds.length})`}
                className='text-sm font-medium h-9 w-[120px]'
                disabled={selectedIds.length === 0}
                onClick={handleConfirm}
              />
            </div>
          </div>
        </div>
        <LoadingOverlay loading={loading} />
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}

